import { useQuery } from '@tanstack/react-query'
import { BuildingIcon, GraduationCapIcon, ShieldCheckIcon, UsersIcon } from 'lucide-react'
import { observer } from 'mobx-react'
import { useContext } from 'react'
import { useParams } from 'react-router'

import { DataTable } from '@/components/DataTable'
import { Button } from '@/components/elements/button'
import { Card, CardContent, CardHeader, CardTitle } from '@/components/elements/card'
import { Skeleton } from '@/components/elements/skeleton'
import { Layout } from '@/components/Layout'
import { getOrganization, getOrganizationSummary } from '@/services/organization.service'
import { updateUser } from '@/services/user.service'
import { UserStoreContext } from '@/stores/UserStore'

const TOTAL_CARDS = [
  { key: 'facilityCount', label: 'Facilities', icon: BuildingIcon },
  { key: 'memberCount', label: 'Members', icon: UsersIcon },
  { key: 'enrolledCount', label: 'Enrolled', icon: GraduationCapIcon },
  { key: 'certifiedCount', label: 'Certified', icon: ShieldCheckIcon },
  { key: 'eapCount', label: 'EAPs', icon: ShieldCheckIcon },
]

const OrganizationDashboard = observer(() => {
  const { organizationId } = useParams()
  const { user, setUser } = useContext(UserStoreContext)

  const organizationQuery = useQuery({
    queryKey: ['organization', organizationId],
    queryFn: () => getOrganization(organizationId),
    enabled: !!organizationId,
  })

  const summaryQuery = useQuery({
    queryKey: ['organization', organizationId, 'summary'],
    queryFn: () => getOrganizationSummary(organizationId),
    enabled: !!organizationId,
  })

  const orgName = organizationQuery.data?.name ?? 'Organization'
  const totals = summaryQuery.data?.totals
  const facilities = summaryQuery.data?.facilities ?? []

  // Switch the user's context into one of the child facilities. PrivateRoute's
  // reaction on activeEntity handles the navigation to that facility.
  const openFacility = async (entityId) => {
    const response = await updateUser({
      id: user.id,
      activeEntity: entityId,
      activeOrganization: null,
    })
    setUser(response)
  }

  if (summaryQuery.isPending) {
    return (
      <Layout breadcrumbs={['Organization', 'Dashboard']}>
        <Layout.Heading title="Organization Dashboard" />
        <div className="grid gap-4 sm:grid-cols-2 lg:grid-cols-5">
          {TOTAL_CARDS.map((card) => (
            <Skeleton key={card.key} className="h-24" />
          ))}
        </div>
        <Skeleton className="mt-6 h-64 w-full" />
      </Layout>
    )
  }

  return (
    <Layout breadcrumbs={['Organization', 'Dashboard']}>
      <Layout.Heading title={orgName} className="mb-6" />

      <div className="grid gap-4 sm:grid-cols-2 lg:grid-cols-5">
        {TOTAL_CARDS.map((card) => (
          <Card key={card.key}>
            <CardHeader className="flex flex-row items-center justify-between space-y-0 pb-2">
              <CardTitle className="text-muted-foreground text-sm font-medium">
                {card.label}
              </CardTitle>
              <card.icon className="text-muted-foreground size-4" />
            </CardHeader>
            <CardContent>
              <div className="text-2xl font-bold">{totals?.[card.key] ?? 0}</div>
            </CardContent>
          </Card>
        ))}
      </div>

      <div className="mt-8">
        <h2 className="mb-3 text-lg font-semibold">Facilities</h2>
        <DataTable
          columns={[
            {
              accessorKey: 'entityName',
              header: 'Facility',
              cell: ({ row }) => <span className="font-medium">{row.original.entityName}</span>,
            },
            { accessorKey: 'memberCount', header: 'Members', size: 110 },
            { accessorKey: 'enrolledCount', header: 'Enrolled', size: 110 },
            { accessorKey: 'certifiedCount', header: 'Certified', size: 110 },
            { accessorKey: 'eapCount', header: 'EAPs', size: 90 },
            {
              id: 'open',
              header: '',
              size: 100,
              cell: ({ row }) => (
                <Button
                  className="place-self-end"
                  variant="outline"
                  size="sm"
                  onClick={() => openFacility(row.original.entityId)}
                >
                  Open
                </Button>
              ),
            },
          ]}
          data={facilities}
          rowCount={facilities.length}
          hidePagination
        />
      </div>
    </Layout>
  )
})

export default OrganizationDashboard
